import { Outlet, Link, NavLink, useNavigate } from "react-router-dom";
import { useMsal, useAccount } from "@azure/msal-react";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";

const LIAISE_LIFE_URL = "https://liaisegroup.sharepoint.com/sites/LiaiseWorkspace";

export function AppShell() {
  const { accounts, instance } = useMsal();
  const account = useAccount(accounts[0]);
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const isAdmin = !!me.data?.roles?.admin;
  const tab = (to: string, label: string) => (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `text-sm px-3 py-1.5 rounded-lg ${
          isActive ? "bg-white text-liaise-navy" : "text-white/80 hover:text-white"
        }`
      }
      end
    >
      {label}
    </NavLink>
  );
  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-liaise-navy text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3 sm:gap-6">
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <img src="/liaise-wordmark-white.svg" alt="Liaise" className="h-7 w-auto" />
            <span className="text-white/40 hidden sm:inline">|</span>
            <span className="font-medium hidden sm:inline">Pricing admin</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1 flex-wrap">
            {tab("/", "Approvals")}
            {isAdmin && tab("/all-estimates", "All estimates")}
            {tab("/reports", "Reports")}
            {isAdmin && <SettingsMenu />}
          </nav>
          <div className="flex-1" />
          <GlobalSearch />
          <a href={LIAISE_LIFE_URL} target="_top" rel="noreferrer"
             className="hidden sm:inline text-xs text-white/70 hover:text-white whitespace-nowrap">
            ← Liaise Life
          </a>
          <div className="flex items-center gap-2 text-sm">
            <span className="opacity-80 hidden lg:inline">{account?.name || account?.username}</span>
            <button onClick={() => instance.logoutRedirect()} className="opacity-80 hover:opacity-100 underline text-xs sm:text-sm">
              Sign out
            </button>
          </div>
        </div>
        {/* Mobile nav drawer */}
        <nav className="md:hidden border-t border-white/10 px-4 py-2 flex flex-wrap gap-1">
          {tab("/", "Approvals")}
          {isAdmin && tab("/all-estimates", "All")}
          {tab("/reports", "Reports")}
          {isAdmin && tab("/rates", "Rates")}
          {isAdmin && tab("/rules", "Rules")}
          {isAdmin && tab("/approvers", "Approvers")}
          {isAdmin && tab("/users", "Users")}
          {isAdmin && tab("/modules", "Modules")}
          {isAdmin && tab("/audit", "Audit")}
        </nav>
      </header>
      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8"><Outlet /></div>
      </main>
    </div>
  );
}

function SettingsMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function clickOutside(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", clickOutside);
    return () => document.removeEventListener("mousedown", clickOutside);
  }, []);

  const items: Array<{ to: string; label: string; desc: string }> = [
    { to: "/rates",     label: "Rates",     desc: "Per-module rate cards, effective-dated." },
    { to: "/rules",     label: "Rules",     desc: "Thresholds, flags, suggestions, system routing." },
    { to: "/approvers", label: "Approvers", desc: "Primary, secondary, OOO cover." },
    { to: "/users",     label: "Users",     desc: "Who can see which pricing modules." },
    { to: "/modules",   label: "Modules",   desc: "Enable / hide pricing surfaces." },
    { to: "/audit",     label: "Audit",     desc: "Every change, every action." },
  ];

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`text-sm px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5 ${
          open ? "bg-white text-liaise-navy" : "text-white/80 hover:text-white"
        }`}
      >
        Settings
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <polyline points="1.5 3.5 5 7 8.5 3.5"/>
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-64 bg-white text-slate-700 rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `block px-4 py-3 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0 ${
                  isActive ? "bg-slate-50" : ""
                }`
              }
            >
              <div className="font-medium text-liaise-navy">{it.label}</div>
              <div className="text-xs text-slate-500 mt-0.5">{it.desc}</div>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function GlobalSearch() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  // Pull all-estimates once; filter client-side.
  const all = useQuery({
    queryKey: ["all-estimates-search"],
    queryFn: () => api.allEstimates(),
    enabled: open,
    staleTime: 60_000,
  });

  useEffect(() => {
    function clickOutside(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", clickOutside);
    return () => document.removeEventListener("mousedown", clickOutside);
  }, []);

  const matches = (() => {
    if (!q.trim()) return [];
    const term = q.trim().toLowerCase();
    const rows: any[] = all.data?.estimates ?? [];
    return rows
      .filter((r) =>
        r.request_number?.toLowerCase().includes(term) ||
        r.claimant_display_name?.toLowerCase().includes(term) ||
        r.claimant_email?.toLowerCase().includes(term) ||
        r.pws_initials?.toLowerCase().includes(term) ||
        r.pws_name?.toLowerCase().includes(term) ||
        r.home_name?.toLowerCase().includes(term) ||
        r.funder_name?.toLowerCase().includes(term)
      )
      .slice(0, 8);
  })();

  return (
    <div ref={ref} className="relative">
      <input
        type="text"
        placeholder="Search estimates..."
        value={q}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        className="px-3 py-1.5 rounded-lg bg-white/10 placeholder:text-white/50 text-white text-sm w-28 sm:w-56 focus:w-72 transition-all focus:outline-none focus:bg-white/20"
      />
      {open && q.trim() && (
        <div className="absolute right-0 mt-2 w-[min(100vw-2rem,28rem)] bg-white text-slate-700 rounded-xl shadow-xl border border-slate-200 max-h-96 overflow-y-auto z-50">
          {all.isLoading && <div className="p-4 text-sm text-slate-500">Loading...</div>}
          {!all.isLoading && matches.length === 0 && <div className="p-4 text-sm text-slate-500">No matches.</div>}
          {matches.map((m) => (
            <button key={m.id} onClick={() => { setOpen(false); setQ(""); nav(`/approvals/${m.id}`); }}
              className="w-full text-left px-4 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0">
              <div className="flex justify-between gap-3">
                <div className="text-sm font-medium text-liaise-navy">{m.request_number}</div>
                <div className="text-xs text-slate-500 capitalize">{m.status}</div>
              </div>
              <div className="text-xs text-slate-500 truncate">
                {m.claimant_display_name} · {m.pws_initials} · {m.home_name ?? "-"} · {m.funder_name ?? "-"}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
